import React, { useState, useCallback, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { adminSubscriptionService } from '../../../services/adminSubscriptionService';

const planOptions = [
  { id: 'monthly', label: 'Monthly (₹2,500)' },
  { id: 'yearly', label: 'Yearly (₹5,000)' },
  { id: 'lifetime', label: 'Lifetime (₹10,000)' },
];

const SubscriptionManagementTab = () => {
  const [search, setSearch] = useState('');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [actionUserId, setActionUserId] = useState(null);
  const [grantPlan, setGrantPlan] = useState('monthly');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: err } = await adminSubscriptionService.list(search.trim());
      if (err) throw err;
      setUsers(data?.users || []);
    } catch (e) {
      setError(e?.message || 'Failed to load users.');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    void load();
  }, []);

  const setStatus = async (targetUserId, mode) => {
    setActionUserId(targetUserId);
    setError(null);
    try {
      const { error: err } = await adminSubscriptionService.setSubscriptionStatus(
        targetUserId,
        mode,
        mode === 'premium' ? grantPlan : 'monthly'
      );
      if (err) throw err;
      await load();
    } catch (e) {
      setError(e?.message || 'Update failed.');
    } finally {
      setActionUserId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-heading font-semibold mb-1">Subscriptions &amp; payments</h2>
        <p className="text-sm text-muted-foreground mb-4">
          Lists profiles with Stripe/UroPay context and lets you grant premium or set free. Data loads with your admin session (PostgREST + RLS). If you see a permission error, apply the latest Supabase migrations for admin subscription access.
        </p>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-end">
        <div className="flex-1">
          <label className="text-xs font-medium text-muted-foreground block mb-1">Search email / username / name</label>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Min 2 characters to filter"
            className="w-full border border-border bg-background rounded-lg px-3 py-2 text-sm"
          />
        </div>
        <Button type="button" onClick={load} disabled={loading}>
          {loading ? 'Loading…' : 'Load / refresh'}
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-muted-foreground">When granting premium:</span>
        <select
          value={grantPlan}
          onChange={(e) => setGrantPlan(e.target.value)}
          className="border border-border bg-background rounded-lg px-2 py-1 text-sm"
        >
          {planOptions.map((p) => (
            <option key={p.id} value={p.id}>
              {p.label}
            </option>
          ))}
        </select>
      </div>

      {error && (
        <div className="rounded-lg border border-error/40 bg-error/10 px-4 py-3 text-sm text-error flex items-start gap-2">
          <Icon name="AlertCircle" size={18} className="shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-sm text-left">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="p-3 font-medium">User</th>
              <th className="p-3 font-medium">Profile status</th>
              <th className="p-3 font-medium">Stripe row</th>
              <th className="p-3 font-medium">UroPay (recent)</th>
              <th className="p-3 font-medium w-[200px]">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 && !loading && (
              <tr>
                <td colSpan={5} className="p-6 text-center text-muted-foreground">
                  No rows yet. Click &quot;Load / refresh&quot;.
                </td>
              </tr>
            )}
            {users.map((u) => {
              const sub = u.stripe_subscriptions;
              const pend = (u.uropay_pending_recent || [])[0];
              const busy = actionUserId === u.id;
              return (
                <tr key={u.id} className="border-b border-border/60 last:border-0">
                  <td className="p-3 align-top">
                    <div className="font-medium">{u.display_name || u.username || '—'}</div>
                    <div className="text-xs text-muted-foreground break-all">{u.email}</div>
                    <div className="text-xs text-muted-foreground font-mono mt-1">{u.id}</div>
                    {u.is_admin && (
                      <span className="inline-block mt-1 text-xs bg-primary/15 text-primary px-2 py-0.5 rounded">Admin</span>
                    )}
                  </td>
                  <td className="p-3 align-top">
                    <div>{u.subscription_status || '—'}</div>
                    <div className="text-xs text-muted-foreground">{u.subscription_plan || ''}</div>
                  </td>
                  <td className="p-3 align-top text-xs">
                    {sub ? (
                      <>
                        <div>{sub.status}</div>
                        <div className="text-muted-foreground">{sub.plan_name}</div>
                        <div className="text-muted-foreground font-mono truncate max-w-[180px]" title={sub.stripe_subscription_id}>
                          {sub.stripe_subscription_id}
                        </div>
                      </>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="p-3 align-top text-xs">
                    {pend ? (
                      <>
                        <div>{pend.status}</div>
                        <div className="text-muted-foreground">{pend.plan_id} · ₹{pend.amount}</div>
                        <div className="text-muted-foreground font-mono truncate max-w-[160px]" title={pend.reference_number || ''}>
                          {pend.reference_number || 'no ref'}
                        </div>
                      </>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="p-3 align-top">
                    <div className="flex flex-col gap-2">
                      <Button
                        type="button"
                        size="sm"
                        variant="default"
                        disabled={busy}
                        onClick={() => setStatus(u.id, 'premium')}
                      >
                        {busy ? '…' : 'Grant premium'}
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={busy}
                        onClick={() => setStatus(u.id, 'free')}
                      >
                        Set free
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SubscriptionManagementTab;
